/**
 * kSearchCore — server-safe search ENGINE: all string matching, ranking, and
 * highlight-STRING generation. No browser APIs (no document / DOM), so it is safe
 * to import in Node.js, Azure Functions, or the browser.
 *
 * Exports:
 *   kSearchItemCore  — one searchable row: an accent-folded SearchableString plus
 *                      CheckMatch (substring test, accent-insensitive).
 *   kSearchItemsCore — the collection: Search / SearchAllWords (incremental filter),
 *                      GetHighlightedText / GetHighlightedTextMultiWord (wrap matches in
 *                      <span class="highlight">), ResetFilters, NonHidden, and the static
 *                      people-name matchers NameMatches (any-order, prefix-per-token) and
 *                      NameScore (relevance ranking) — both delegating to kCore's fold +
 *                      tokenize primitives (NormalizeName / NameTokens).
 *
 * WHERE THESE SIT IN THE CHAIN (do not "simplify" it back):
 *
 *     kObjectCore      -> kSearchItemCore  ->  (browser) kObject      -> kSearchItem
 *                                          ->  kSPItemCore
 *     kObjectArrayCore -> kSearchItemsCore ->  (browser) kObjectArray -> kSearchItems
 *
 * ITEM side: MATCHING (SearchableString / FilteredOut / CheckMatch) lives on kObjectCore
 * itself, because kObjectArrayCore.Items is kObjectCore[] and the engine iterates exactly
 * that. BUILDING the text to match against lives on kSearchItemCore, which kObject and
 * kSPItemCore both extend - so every browser object and every SharePoint list item can
 * populate its own SearchableString. kSearchItem stays a thin browser marker.
 * COLLECTION side: the engine lives here, and the browser kObjectArray extends this class,
 * so every browser collection inherits it and kSearchItems is once again just kObjectArray
 * plus the DOM bits (the .highlight stylesheet and the kSearchInput widget).
 *
 * The 2026-08-22 change was ADDITIVE in both places: kSearchItemCore gained members and no
 * class lost any. That is the direction that is safe. Moving matching DOWN onto a subclass
 * is the direction that broke everything in 2026-07-30 - see the account below.
 *
 * An earlier refactor hung kSearchItem / kSearchItems straight off the two core classes.
 * That cut them off from kObject / kObjectArray and broke every consumer passing a
 * search-backed collection where a kObjectArray is expected (Kanban Task Manager's
 * KTMProjects / KTMPhases / KTMSPResponsibles, found 2026-07-30). Do not re-apply it.
 *
 * Searchable strings and search terms are folded with kCore.FoldAccents (accent-
 * insensitive but digraph-preserving), so "Zürich" matches "zurich" without corrupting
 * ordinary words. The NAME matchers additionally apply kCore.NormalizeName's German
 * digraph collapse so "Mueller" matches "Müller".
 */

import { getCoreUtils, kCore, nz } from "./kCoreHandler";
import { kRegexStringCore } from "./kRegexStringCore";
import { kObjectCore, kObjectArrayCore } from "./kCoreObjects";

// ── kSearchItemCore ──────────────────────────────────────────────────────────

/**
 * One searchable row: it knows how to BUILD the text the engine matches against.
 *
 * MATCHING lives on kObjectCore (SearchableString / FilteredOut / CheckMatch) and must stay
 * there - kObjectArrayCore.Items is kObjectCore[], so the engine below iterates exactly that
 * and needs no cast. POPULATING lives here, because nothing in the engine calls it: it is
 * opt-in behaviour a consumer invokes, never something kObjectCore[] must provide. That
 * asymmetry is the whole reason this split is safe (see the 2026-07-30 account above - the
 * breakage then was moving matching DOWN, not adding building HERE).
 *
 * WHY THIS EXISTS (2026-08-22). The engine could match but nothing could populate: no file
 * in the shared libraries and no product in the estate ever assigned SearchableString. It
 * defaults to "", and CheckMatch answers `"".indexOf(Term) === -1` -> FilteredOut = true, so
 * Search() on any collection hid EVERY row and looked like a working filter that found
 * nothing. Every consumer would otherwise hand-roll the same concatenate-these-fields loop.
 *
 *     const Contract = new CONContract();
 *     Contract.SearchFields = ["ContractNumber", "Title", "Department"];
 *     Contract.BuildSearchableString();          // idempotent - call it whenever a field changes
 *
 * BuildSearchableString is IDEMPOTENT by construction: it always REPLACES SearchableString
 * from SearchFields plus whatever AddSearchText contributed. Appending instead would grow the
 * string without bound the first time a consumer rebuilt inside a render loop.
 *
 * It reads ONE level deep and NEVER recurses into an object graph. A nested object
 * contributes its Title and nothing else; a nested array is skipped. That is deliberate:
 * object graphs in this estate may contain CYCLES (ContractsManager's RelatedContracts is
 * symmetric and explicitly may), and a recursive text builder would hang on the first one.
 */
export class kSearchItemCore extends kObjectCore {
    // Property names on THIS object that feed the searchable text. Transient - see
    // kObjectCore.TransientProperties, which excludes it from ToJSONObj.
    public SearchFields: string[] = [];

    // Text contributed by AddSearchText: a resolved lookup label, a computed value - anything
    // that is not held in a property of its own. Re-applied on every build.
    private _ExtraSearchText: string = "";

    /**
     * Replace SearchableString with the text of every property named in SearchFields, plus
     * anything AddSearchText contributed. Safe to call repeatedly.
     */
    public BuildSearchableString = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Self = this as any;
            const Parts: string[] = [];
            let FieldName: string;
            for (FieldName of this.SearchFields) {
                const Text: string = this._ValueToSearchText(Self[FieldName]);
                if (Text !== "") { Parts.push(Text); }
            }
            if (this._ExtraSearchText !== "") { Parts.push(this._ExtraSearchText); }
            // Assignment goes through kObjectCore's setter, which folds accents.
            this.SearchableString = Parts.join(" ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemCore.BuildSearchableString");
        }
    };

    /**
     * Add searchable text that is not held in a property. Order-independent: it rebuilds, so
     * it works whether it is called before or after BuildSearchableString.
     */
    public AddSearchText = (Text: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Trimmed: string = nz(Text).trim();
            if (Trimmed === "") { return; }
            if (this._ExtraSearchText === "") {
                this._ExtraSearchText = Trimmed;
            } else {
                this._ExtraSearchText = this._ExtraSearchText + " " + Trimmed;
            }
            this.BuildSearchableString();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemCore.AddSearchText");
        }
    };

    /** Drop the AddSearchText contributions and rebuild from SearchFields alone. */
    public ClearSearchText = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this._ExtraSearchText = "";
            this.BuildSearchableString();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemCore.ClearSearchText");
        }
    };

    /** One value to its searchable text. Returns "" for anything with no text to give. */
    private _ValueToSearchText = (Value: any): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Value === null || Value === undefined) { return ""; }
            if (typeof Value === "string") { return Value; }
            if (typeof Value === "number") { return String(Value); }
            if (typeof Value === "boolean") { return String(Value); }
            if (typeof Value === "function") { return ""; }
            if (Array.isArray(Value)) { return this._ArrayToSearchText(Value); }
            return this._ObjectToSearchText(Value);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemCore._ValueToSearchText");
            return "";
        }
    };

    /**
     * An object contributes a date, or its Title, or nothing. It does NOT contribute its own
     * SearchableString and it is NOT walked - see the cycle note in the class comment.
     */
    private _ObjectToSearchText = (Value: any): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let ConstructorName: string = "";
            if (Value.constructor !== null && Value.constructor !== undefined) {
                ConstructorName = Value.constructor.name;
            }
            if (ConstructorName === "kTime" || ConstructorName === "kTimeCore") {
                return nz(Value.ToISODateTime());
            }
            if (Value instanceof Date) { return Value.toISOString(); }
            if (typeof Value.Title === "string") { return Value.Title; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemCore._ObjectToSearchText");
            return "";
        }
    };

    /** Each entry's text, joined. Nested arrays are skipped rather than flattened. */
    private _ArrayToSearchText = (Values: any[]): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const Parts: string[] = [];
            let Entry: any;
            for (Entry of Values) {
                if (Array.isArray(Entry)) { continue; }
                const Text: string = this._ValueToSearchText(Entry);
                if (Text !== "") { Parts.push(Text); }
            }
            return Parts.join(" ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemCore._ArrayToSearchText");
            return "";
        }
    };
}

// ── kSearchItemsCore ─────────────────────────────────────────────────────────

export class kSearchItemsCore extends kObjectArrayCore {
    // Items is deliberately NOT narrowed to kSearchItemCore[]. The browser kObjectArray
    // extends this class, so narrowing here would narrow every kObjectArray subclass in
    // every product - including the ones whose items extend kObjectCore through a core
    // chain rather than through kSearchItemCore. The engine casts per item instead, the
    // same way kObjectArray._NextCallback casts to kObject.
    public PrevSearchTerm: string = "";
    public CurrSearchTerm: string = "";
    public FilteredItems: number = 0;
    public MatchesCount: number = 0;

    /**
     * Build every item's SearchableString before searching. Call it once after loading a
     * collection, and again whenever the underlying values change.
     *
     * Items is kObjectCore[], and only items that extend kSearchItemCore can build - so this
     * tests for the method rather than casting blindly. A collection of plain kObjectCore is
     * left untouched instead of throwing, which is the correct outcome and not a swallowed
     * error: such an item has no fields declared and nothing to build from.
     */
    public BuildSearchableStrings = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            let CurrItem: any;
            for (CurrItem of this.Items) {
                if (typeof CurrItem.BuildSearchableString === "function") {
                    CurrItem.BuildSearchableString();
                }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemsCore.BuildSearchableStrings");
        }
    };

    public ResetFilters = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            let CurrItem: kObjectCore;
            for (CurrItem of this.Items) {
                CurrItem.FilteredOut = false;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemsCore.ResetFilters");
            return;
        }
    };

    public Search = (SearchTerm: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            let CurrItem: kObjectCore;
            this.CurrSearchTerm = SearchTerm;
            if (this.CurrSearchTerm === "" || this.CurrSearchTerm.length < this.PrevSearchTerm.length) {
                this.ResetFilters();
            } else {
                if (this.PrevSearchTerm.startsWith(this.CurrSearchTerm) === false) {
                    this.ResetFilters();
                }
            }
            this.MatchesCount = this.Items.length;
            for (CurrItem of this.Items) {
                if (CurrItem.FilteredOut === false) {
                    if (CurrItem.CheckMatch(this.CurrSearchTerm) === false) {
                        this.MatchesCount--;
                    }
                } else {
                    this.MatchesCount--;
                }
            }
            this.FilteredItems = this.Items.length - this.MatchesCount;
            this.PrevSearchTerm = this.CurrSearchTerm;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemsCore.Search");
            return;
        }
    };

    public SearchAllWords = (SearchTerm: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (SearchTerm.indexOf(" ") === -1) {
                this.Search(SearchTerm);
            } else {
                let CurrItem: kObjectCore;
                this.CurrSearchTerm = SearchTerm;
                const SearchWords: string[] = SearchTerm.split(" ");
                let CurrSearchWord: string = "";
                let WordsFound: number = 0;
                if (this.CurrSearchTerm === "" || this.CurrSearchTerm.length < this.PrevSearchTerm.length) {
                    this.ResetFilters();
                } else {
                    if (this.PrevSearchTerm.startsWith(this.CurrSearchTerm) === false) {
                        this.ResetFilters();
                    }
                }
                this.MatchesCount = this.Items.length;
                for (CurrItem of this.Items) {
                    if (CurrItem.FilteredOut === false) {
                        WordsFound = 0;
                        for (CurrSearchWord of SearchWords) {
                            if (CurrItem.CheckMatch(CurrSearchWord) === true) {
                                WordsFound++;
                                CurrItem.FilteredOut = false;
                            }
                        }
                        if (WordsFound === SearchWords.length) {
                            CurrItem.FilteredOut = false;
                        } else {
                            this.MatchesCount--;
                            CurrItem.FilteredOut = true;
                        }
                    } else {
                        this.MatchesCount--;
                    }
                }
                this.FilteredItems = this.Items.length - this.MatchesCount;
                this.PrevSearchTerm = this.CurrSearchTerm;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemsCore.SearchAllWords");
            return;
        }
    };

    public GetHighlightedText = (TextToHighlight: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (this.CurrSearchTerm === "") { return TextToHighlight; }
            const Escaped: string = this._EscapeRegex(this.CurrSearchTerm);
            const Pattern: RegExp = new RegExp("(" + Escaped + ")", "gi");
            return TextToHighlight.replace(Pattern, "<span class=\"highlight\">$1</span>");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemsCore.GetHighlightedText");
            return TextToHighlight;
        }
    };

    public GetHighlightedTextMultiWord = (TextToHighlight: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (this.CurrSearchTerm.indexOf(" ") === -1) {
                return this.GetHighlightedText(TextToHighlight);
            }
            const SearchWords: string[] = this.CurrSearchTerm.split(" ");
            const ValidWords: string[] = [];
            let Word: string;
            for (Word of SearchWords) {
                if (Word.length > 0) { ValidWords.push(this._EscapeRegex(Word)); }
            }
            if (ValidWords.length === 0) { return TextToHighlight; }
            const Pattern: RegExp = new RegExp("(" + ValidWords.join("|") + ")", "gi");
            return TextToHighlight.replace(Pattern, "<span class=\"highlight\">$1</span>");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemsCore.GetHighlightedTextMultiWord");
            return TextToHighlight;
        }
    };

    private _EscapeRegex = (Text: string): string => {
        return kRegexStringCore.Escape(Text);
    };

    public NonHidden = (): kSearchItemsCore => {
        const NonHiddenItems: kSearchItemsCore = new kSearchItemsCore();
        if (getCoreUtils().KErrorMode) { return new kSearchItemsCore(); }
        try {
            let CurrItem: kObjectCore;
            for (CurrItem of this.Items) {
                if (CurrItem.FilteredOut !== true) {
                    NonHiddenItems.push(CurrItem);
                }
            }
            NonHiddenItems.SortByProperty("Sequence", true, true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemsCore.NonHidden");
        }
        return NonHiddenItems;
    };

    // ── People-name matchers (relocated from kCore) ───────────────────────────
    // These build on kCore.NameTokens / NormalizeName (the German-fold + tokenize
    // primitives) and power the people-picker's in-memory pre-filter and ranking.

    // True when EVERY query token is a prefix of some name/email token (any order).
    // "adrian f" / "f adrian" / "fuchs a" all match "Fuchs Adrian".
    public static NameMatches = (Query: string, Name: string, Email?: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            const QTokens: string[] = kCore.NameTokens(Query);
            if (QTokens.length === 0) { return false; }
            const CTokens: string[] = kCore.NameTokens(Name, Email);
            let QToken: string;
            for (QToken of QTokens) {
                let Found: boolean = false;
                let CToken: string;
                for (CToken of CTokens) {
                    if (CToken.indexOf(QToken) === 0) { Found = true; }
                }
                if (Found === false) { return false; }
            }
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemsCore.NameMatches");
            return false;
        }
    };

    // Relevance score for ranking: each query token scores its best candidate-token
    // match (exact=3, prefix=2, substring=1, none=0); +5 when every query token matched;
    // +2 when the first typed token starts the candidate's first or last name token.
    // Higher is better; 0 = no match.
    public static NameScore = (Query: string, Name: string, Email?: string): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            const QTokens: string[] = kCore.NameTokens(Query);
            if (QTokens.length === 0) { return 0; }
            const CTokens: string[] = kCore.NameTokens(Name, Email);
            if (CTokens.length === 0) { return 0; }
            let Total: number = 0;
            let AllMatched: boolean = true;
            let QToken: string;
            for (QToken of QTokens) {
                let Best: number = 0;
                let CToken: string;
                for (CToken of CTokens) {
                    if (CToken === QToken) { if (Best < 3) { Best = 3; } }
                    else if (CToken.indexOf(QToken) === 0) { if (Best < 2) { Best = 2; } }
                    else if (CToken.indexOf(QToken) > 0) { if (Best < 1) { Best = 1; } }
                }
                if (Best === 0) { AllMatched = false; }
                Total = Total + Best;
            }
            if (AllMatched === true) { Total = Total + 5; }
            const NameOnly: string[] = kCore.NameTokens(Name);
            if (NameOnly.length > 0) {
                if (NameOnly[0].indexOf(QTokens[0]) === 0) { Total = Total + 2; }
                if (NameOnly[NameOnly.length - 1].indexOf(QTokens[0]) === 0) { Total = Total + 2; }
            }
            return Total;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSearchItemsCore.NameScore");
            return 0;
        }
    };
}
